import React, { useCallback, useState } from "react";
import MainHeader from "./Common/MainHeader";
import Footer from "./Common/Footer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as myConstList from "./Common/BaseUrl";
import { Link } from "react-router-dom";
import { ProductName } from "./Common/ProductName";
import DocumentMeta from "react-document-meta";
import { useContactUsMutation } from "../redux/userApi";

const meta = {
  title: `Contact Us | ${ProductName}  Exchange `,
  description:
    "Get A User Friendly Crypto Exchange App, A Local Crypto Exchange Platform In India For Bitcoin Exchange And Other Crypto Currencies Trading. Sign Up Now.",
  canonical: `https://${ProductName}.live/contact-us-for-crypto-exchange`,
  meta: {
    charset: "utf-8",
    name: {
      keywords:
        "Crypto Exchange, Local Crypto Exchange, BTC Exchange, Bitcoin Exchange",
    },
  },
};

const emailReg =
  /^([a-zA-Z\d\.-]+)@([a-zA-Z\.-]+)\.([a-zA-Z]{2,8})([a-zA-Z]{2,8})?$/;

const trimError = (fieldName) => `Please enter a valid ${fieldName}`;

const ContactUs = () => {
  // form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);

  // errors
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [messageError, setMessageError] = useState("");

  const [sendMessage] = useContactUsMutation();

  const resetForm = useCallback(() => {
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
    setNameError("");
    setEmailError("");
    setSubjectError("");
    setMessageError("");
  }, []);

  // helper validators (place in the same component scope)
  const hasLeadingOrTrailing = (v) => v !== v.trim();
  const hasConsecutiveSpaces = (v) => / {2,}/.test(v);
  const isEmpty = (v) => !v || v.trim().length === 0;
  const lengthBetween = (v, min = 0, max = Infinity) => {
    const len = v.length;
    return len >= min && len <= max;
  };

  // -- replace validateFields --
  const validateFields = useCallback(() => {
    // clear previous errors first (optional, but keeps behavior predictable)
    setNameError("");
    setEmailError("");
    setSubjectError("");
    setMessageError("");

    // Name
    if (isEmpty(name)) {
      setNameError("This field is required");
      return false;
    }
    if (hasLeadingOrTrailing(name)) {
      setNameError("Please remove leading or trailing spaces");
      return false;
    }
    if (hasConsecutiveSpaces(name)) {
      setNameError("Please do not use consecutive spaces");
      return false;
    }
    if (!lengthBetween(name, 2, 50)) {
      if (name.length < 2) setNameError("Name must be at least 2 characters");
      else setNameError("Name must be at most 50 characters");
      return false;
    }

    let valid = true;

    if (isEmpty(email)) {
      setEmailError("This field is required");
      valid = false;
    } else if (email.startsWith(" ")) {
      setEmailError("Please remove leading spaces");
      valid = false;
    } else if (email.endsWith(" ")) {
      setEmailError("Please remove trailing spaces");
      valid = false;
    } else if (email.length > 100) {
      setEmailError("Email must be at most 100 characters");
      valid = false;
    } else if (/\s/.test(email)) {
      setEmailError("Email must not contain spaces");
      valid = false;
    } else if (!emailReg.test(email)) {
      setEmailError("Please enter valid email");
      valid = false;
    }

    // Subject
    if (isEmpty(subject)) {
      setSubjectError("This field is required");
      return false;
    }
    if (hasLeadingOrTrailing(subject)) {
      setSubjectError("Please remove leading or trailing spaces");
      return false;
    }
    if (hasConsecutiveSpaces(subject)) {
      setSubjectError("Please do not use consecutive spaces");
      return false;
    }
    if (!lengthBetween(subject, 2, 100)) {
      if (subject.length < 2)
        setSubjectError("Subject must be at least 2 characters");
      else setSubjectError("Subject must be at most 100 characters");
      return false;
    }

    // Message
    if (isEmpty(message)) {
      setMessageError("This field is required");
      return false;
    }
    if (hasLeadingOrTrailing(message)) {
      setMessageError("Please remove leading or trailing spaces");
      return false;
    }
    if (hasConsecutiveSpaces(message)) {
      setMessageError("Please do not use consecutive spaces");
      return false;
    }
    if (message.length > 300) {
      setMessageError("Message must be at most 300 characters");
      return false;
    }

    // all good
    return true;
  }, [name, email, subject, message]);

  // -- replace handleChange --
  const handleChange = useCallback((e) => {
    const { name: field, value } = e.target;

    // immediate inline validation messages (keeps UX similar to before)
    switch (field) {
      case "name": {
        setName(value);
        if (isEmpty(value)) setNameError("This field is required");
        else if (hasLeadingOrTrailing(value))
          setNameError("Please remove leading or trailing spaces");
        else if (hasConsecutiveSpaces(value))
          setNameError("Please do not use consecutive spaces");
        else if (!lengthBetween(value, 2, 50)) {
          if (value.length < 2)
            setNameError("Name must be at least 2 characters");
          else setNameError("Name must be at most 50 characters");
        } else setNameError("");
        break;
      }
      case "email": {
        setEmail(value);
        if (isEmpty(value)) {
          setEmailError("This field is required");
        } else if (value.startsWith(" ")) {
          setEmailError("Please remove leading spaces");
        } else if (value.endsWith(" ")) {
          setEmailError("Please remove trailing spaces");
        } else if (/\s/.test(value)) {
          setEmailError("Email must not contain spaces");
        } else if (!emailReg.test(value)) {
          setEmailError("Please enter valid email");
        } else {
          setEmailError("");
        }

        break;
      }
      case "subject": {
        setSubject(value);
        if (isEmpty(value)) setSubjectError("This field is required");
        else if (hasLeadingOrTrailing(value))
          setSubjectError("Please remove leading or trailing spaces");
        else if (hasConsecutiveSpaces(value))
          setSubjectError("Please do not use consecutive spaces");
        else if (!lengthBetween(value, 2, 100)) {
          if (value.length < 2)
            setSubjectError("Subject must be at least 2 characters");
          else setSubjectError("Subject must be at most 100 characters");
        } else setSubjectError("");
        break;
      }
      case "message": {
        setMessage(value);
        if (isEmpty(value)) setMessageError("This field is required");
        else if (hasLeadingOrTrailing(value))
          setMessageError("Please remove leading or trailing spaces");
        else if (hasConsecutiveSpaces(value))
          setMessageError("Please do not use consecutive spaces");
        else if (value.length > 300)
          setMessageError("Message must be at most 300 characters");
        else setMessageError("");
        break;
      }
      default:
        break;
    }
  }, []);

  // -- replace submitEmail --
  const submitEmail = useCallback(
    async (e) => {
      e.preventDefault();

      // reset inline errors before validating
      setNameError("");
      setEmailError("");
      setSubjectError("");
      setMessageError("");

      if (!validateFields()) return;

      setBtnDisable(true);
      try {
        const payload = {
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
        };

        // send via RTK mutation (single source of truth)
        const res = await sendMessage(payload).unwrap();

        toast.dismiss();
        resetForm();
        toast.success(res?.message || "Mail sent successfully");
      } catch (error) {
        toast.dismiss();
        resetForm();
        toast.error(
          error?.data?.message || error?.message || "Mail sending failed"
        );
      } finally {
        // keep experience consistent with previous behavior (short delay)
        setTimeout(() => setBtnDisable(false), 800);
      }
    },
    [name, email, subject, message, sendMessage, resetForm, validateFields]
  );

  return (
    <div>
      <DocumentMeta {...meta}>
        <div>
          <MainHeader />
          <div className="privacy_policy">
            <div className="container container2">
              <div className="wrapper mt-4">
                <div className="row no-gutters">
                  <div className="col-md-7 d-flex align-items-stretch">
                    <div className="contact-wrap w-100 p-md-5 p-4">
                      <h3 className="mb-4">Get in touch</h3>
                      <div id="form-message-warning" className="mb-4" />
                      <form onSubmit={submitEmail}>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control"
                                name="name"
                                id="name"
                                placeholder="Name"
                                value={name}
                                onChange={handleChange}
                              />
                              <div className="text-danger">{nameError}</div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <input
                                type="email"
                                className="form-control"
                                name="email"
                                id="email"
                                placeholder="Email"
                                value={email}
                                onKeyDown={(e) => {
                                  if (e.key === " ") {
                                    e.preventDefault();
                                  }
                                }}
                                onChange={handleChange}
                              />
                              <div className="text-danger">{emailError}</div>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control"
                                name="subject"
                                id="subject"
                                placeholder="Subject"
                                value={subject}
                                onChange={handleChange}
                              />
                              <div className="text-danger">{subjectError}</div>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <textarea
                                name="message"
                                className="form-control"
                                id="message"
                                cols={30}
                                rows={7}
                                placeholder="Message"
                                value={message}
                                onChange={handleChange}
                              />
                              <div className="text-danger">{messageError}</div>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <input
                                type="submit"
                                className="btn btn_man "
                                disabled={btnDisable}
                              />
                              <div className="submitting" />
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                  <div className="col-md-5 d-flex align-items-stretch">
                    <div className="info-wrap bg_contact w-100 p-lg-5 p-4">
                      <h3 className="mb-4 mt-md-4">Contact us</h3>
                      <div className="dbox w-100 d-flex align-items-start mb-2">
                        <div className="icon d-flex align-items-center justify-content-center">
                          <span className="fa fa-map-marker" />
                        </div>
                        <div className="text pl-3 ">
                          <p className="mt-0">
                            <span>Address: </span>
                            HM 1213
                            <br /> Urban estate, phase 1 , model town, bathinda-
                            151001
                          </p>
                        </div>
                      </div>
                      <div className="dbox w-100 d-flex align-items-center mb-3">
                        <div className="icon d-flex align-items-center justify-content-center">
                          <span className="fa fa-phone" />
                        </div>
                        <div className="text pl-3">
                          <p>
                            <span>Phone:</span>{" "}
                            <h6 to="tel:#">+91 9417326700</h6>
                          </p>
                        </div>
                      </div>
                      <div className="dbox w-100 d-flex align-items-center">
                        <div className="icon d-flex align-items-center justify-content-center">
                          <span className="fa fa-paper-plane" />
                        </div>
                        <div className="text pl-3">
                          <p>
                            <span>Email:</span>{" "}
                            <h6 to="mailto:support@avonx.com">
                              support@Green Crypto X.io
                            </h6>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          <ToastContainer />
        </div>
      </DocumentMeta>
    </div>
  );
};

export default ContactUs;
